"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isUsageOptedOutForProjectType = exports.resolveDocumentAccessFrom = exports.resolveDocumentAccess = exports.BASE_DOCUMENT_ACCESS_RULES = exports.DOCUMENT_ACCESS_RULES = exports.SENT_DOSSIER_USAGES = exports.HUB_FORBIDDEN_USAGES = void 0;
const account_type_1 = require("../accounts/account-type");
const steps_1 = require("../project/steps");
const order_1 = require("../project/order");
const usage_1 = require("./usage");
const requirements_1 = require("./requirements");
const mime_1 = require("./mime");
const shared_documents_1 = require("./shared-documents");
const ALL_UPLOAD_PAGES = ['documents', 'matrix'];
// ── Resolution ──────────────────────────────────────────────────────────────
const ALL_USAGES = Object.values(usage_1.ProjectFileUsage);
const ALL_STEPS = order_1.PROJECT_STEP_ORDER;
/** Requestors that make an urban analysis belong to the client side. */
const CLIENT_SIDE_REQUESTORS = new Set([
    account_type_1.AccountType.CLIENT,
    account_type_1.AccountType.SALESMAN,
]);
const conditionHolds = (condition, ctx) => {
    switch (condition) {
        // An absent input satisfies the condition — see DocumentAccessContext.
        case 'non-empty':
            return ctx.files === undefined ? true : ctx.files.length > 0;
        case 'has-accepted-file':
            return ctx.files === undefined
                ? true
                : ctx.files.some((file) => file.status === 'ACCEPTED');
        case 'has-rejected-file':
            return ctx.files === undefined
                ? true
                : ctx.files.some((file) => file.status === 'REJECTED');
        case 'urban-analysis-delivered':
            return ctx.hasUrbanAnalysis === undefined ? true : ctx.hasUrbanAnalysis;
        case 'urban-analysis-ordered-by-client-side':
            return ctx.urbanAnalysisRequestor === undefined
                ? true
                : ctx.urbanAnalysisRequestor != null &&
                    CLIENT_SIDE_REQUESTORS.has(ctx.urbanAnalysisRequestor);
        default:
            return true;
    }
};
const predicateHolds = (predicate, ctx) => typeof predicate === 'string'
    ? conditionHolds(predicate, ctx)
    : !conditionHolds(predicate.not, ctx);
/** Whether a grant takes access away rather than giving it. */
const narrows = (facet) => facet.visible === false || facet.editable === false;
const ruleMatches = (rule, usage, ctx) => {
    if (rule.usages && !rule.usages.includes(usage))
        return false;
    // An absent role or step makes the query structural: "could anyone see this
    // slot at any step?" — the union over the missing dimension, which is what the
    // missing-document alerts and the triage picker ask for. A rule NARROWING one
    // role or one step cannot reduce a union, so it is skipped; role-agnostic
    // rules still apply either way.
    if (rule.accountTypes) {
        if (ctx.accountType == null) {
            if (narrows(rule.grant))
                return false;
        }
        else if (!rule.accountTypes.includes(ctx.accountType))
            return false;
    }
    if (rule.steps) {
        if (ctx.step == null) {
            if (narrows(rule.grant))
                return false;
        }
        else if (!rule.steps.includes(ctx.step))
            return false;
    }
    // An absent project type does NOT match a type-scoped rule: a POOL's opt-out
    // must not shed a requirement when the type is unknown.
    if (rule.projectTypes) {
        if (ctx.projectType == null)
            return false;
        if (!rule.projectTypes.includes(ctx.projectType))
            return false;
    }
    if (rule.when && !rule.when.every((p) => predicateHolds(p, ctx)))
        return false;
    return true;
};
const resolveFrom = (rules, usage, ctx) => {
    let visible = false;
    let editable = false;
    let required = 0;
    let uploadPages = ALL_UPLOAD_PAGES;
    let mimeTypes;
    let archived = false;
    for (const rule of rules) {
        if (!ruleMatches(rule, usage, ctx))
            continue;
        const { grant } = rule;
        if (grant.visible !== undefined)
            visible = grant.visible;
        if (grant.editable !== undefined)
            editable = grant.editable;
        if (grant.required !== undefined)
            required = grant.required;
        if (grant.uploadPages !== undefined)
            uploadPages = grant.uploadPages;
        if (grant.mimeTypes !== undefined)
            mimeTypes = grant.mimeTypes;
        if (grant.archived !== undefined)
            archived = grant.archived;
    }
    return {
        visible,
        editable,
        required,
        uploadPages,
        mimeTypes,
        archived: archived && visible,
    };
};
// ── Step windows ────────────────────────────────────────────────────────────
/** While the request is in the client side's hands they may still upload. */
const INTAKE_STEPS = [
    steps_1.ProjectStep.PROJECT_INITIALIZED,
    steps_1.ProjectStep.PROJECT_REFUSED,
];
/** The architect drafts the dossier here, and follows it from here on. */
const DRAFTING_ONWARDS = [
    steps_1.ProjectStep.PROJECT_IN_PROGRESS,
    steps_1.ProjectStep.PROJECT_FILE_COMPLETED,
    steps_1.ProjectStep.PROJECT_FILE_SENT,
    steps_1.ProjectStep.PROJECT_FILE_SUBMITTED,
    steps_1.ProjectStep.PROJECT_FILE_REFUSED,
];
/**
 * The commercial follows the architect-produced dossier from the moment drafting
 * is finished (#580) — not while it is still in progress.
 */
const DOSSIER_READY_ONWARDS = [
    steps_1.ProjectStep.PROJECT_FILE_COMPLETED,
    steps_1.ProjectStep.PROJECT_FILE_SENT,
    steps_1.ProjectStep.PROJECT_FILE_SUBMITTED,
    steps_1.ProjectStep.PROJECT_FILE_REFUSED,
];
/** The client only sees the dossier once it has gone to the town hall. */
const DEPOSITED_ONWARDS = [
    steps_1.ProjectStep.PROJECT_FILE_SENT,
    steps_1.ProjectStep.PROJECT_FILE_SUBMITTED,
    steps_1.ProjectStep.PROJECT_FILE_REFUSED,
];
/** The deposit receipt only exists once the dossier has been deposited. */
const POST_VALIDATION_STEPS = (0, order_1.stepsAtLeast)(steps_1.ProjectStep.PROJECT_FILE_SENT);
// ── Usage groups ────────────────────────────────────────────────────────────
// These eleven groups are the distinct access shapes the pre-existing config
// actually had — see the characterization test.
/**
 * The dossier the architect produces: CERFA forms, the PC/DP matrices and every
 * matrix slot (plans, façades, photo placeholders).
 */
const ARCHITECT_DOSSIER_PIECES = [
    usage_1.ProjectFileUsage.CERFA_DP,
    usage_1.ProjectFileUsage.CERFA_PC,
    usage_1.ProjectFileUsage.CERFA_PCM,
    usage_1.ProjectFileUsage.CERFA_13703_11,
    usage_1.ProjectFileUsage.CERFA_13411_11,
    usage_1.ProjectFileUsage.CERFA_13406_12,
    usage_1.ProjectFileUsage.OTHER_APPLICANTS_SHEET,
    usage_1.ProjectFileUsage.PC_MATRIX,
    usage_1.ProjectFileUsage.PCMI1,
    usage_1.ProjectFileUsage.PCMI2,
    usage_1.ProjectFileUsage.PCMI2_1,
    usage_1.ProjectFileUsage.PCMI3,
    usage_1.ProjectFileUsage.PCMI4,
    usage_1.ProjectFileUsage.PCMI5_1,
    usage_1.ProjectFileUsage.PCMI5_2,
    usage_1.ProjectFileUsage.PCMI5_3,
    usage_1.ProjectFileUsage.PCMI5_4,
    usage_1.ProjectFileUsage.PCMI6,
    usage_1.ProjectFileUsage.PCMI78,
    usage_1.ProjectFileUsage.DP_MATRIX,
    usage_1.ProjectFileUsage.DP1,
    usage_1.ProjectFileUsage.DP2,
    usage_1.ProjectFileUsage.DP2_1,
    usage_1.ProjectFileUsage.DP3,
    usage_1.ProjectFileUsage.DP4_1,
    usage_1.ProjectFileUsage.DP4_2,
    usage_1.ProjectFileUsage.DP4_3,
    usage_1.ProjectFileUsage.DP4_4,
    usage_1.ProjectFileUsage.DP6,
    usage_1.ProjectFileUsage.DP78,
    usage_1.ProjectFileUsage.DP11,
    usage_1.ProjectFileUsage.GROUND_PLAN,
    usage_1.ProjectFileUsage.CURRENT_GROUND_PLAN,
    usage_1.ProjectFileUsage.FUTUR_GROUND_PLAN,
    usage_1.ProjectFileUsage.FIELD_CUT,
    usage_1.ProjectFileUsage.CITY_LOCATION_PLAN,
    usage_1.ProjectFileUsage.DISTRICT_LOCATION_PLAN,
    usage_1.ProjectFileUsage.CURRENT_FACADE_1,
    usage_1.ProjectFileUsage.CURRENT_FACADE_2,
    usage_1.ProjectFileUsage.CURRENT_FACADE_3,
    usage_1.ProjectFileUsage.CURRENT_FACADE_4,
    usage_1.ProjectFileUsage.CURRENT_FACADE_5,
    usage_1.ProjectFileUsage.CURRENT_FACADE_6,
    usage_1.ProjectFileUsage.CURRENT_FACADE_7,
    usage_1.ProjectFileUsage.CURRENT_FACADE_8,
    usage_1.ProjectFileUsage.FUTUR_FACADE_1,
    usage_1.ProjectFileUsage.FUTUR_FACADE_2,
    usage_1.ProjectFileUsage.FUTUR_FACADE_3,
    usage_1.ProjectFileUsage.FUTUR_FACADE_4,
    usage_1.ProjectFileUsage.FUTUR_FACADE_5,
    usage_1.ProjectFileUsage.FUTUR_FACADE_6,
    usage_1.ProjectFileUsage.FUTUR_FACADE_7,
    usage_1.ProjectFileUsage.FUTUR_FACADE_8,
    usage_1.ProjectFileUsage.PICTURE_1,
    usage_1.ProjectFileUsage.PICTURE_2,
    usage_1.ProjectFileUsage.PICTURE_3,
    usage_1.ProjectFileUsage.PICTURE_4,
    usage_1.ProjectFileUsage.PICTURE_5,
    usage_1.ProjectFileUsage.PICTURE_6,
];
/**
 * The intake set the client side supplies: dossier photos, the drawing views,
 * the power of attorney, the commercial dossier and the "Autres documents"
 * catch-all.
 */
const CLIENT_INTAKE_PIECES = [
    usage_1.ProjectFileUsage.HOUSE_PICTURE,
    usage_1.ProjectFileUsage.STREET_PICTURE,
    usage_1.ProjectFileUsage.GARDEN_PICTURE,
    usage_1.ProjectFileUsage.CLIENT_PROXY,
    usage_1.ProjectFileUsage.SALES_FILE,
    usage_1.ProjectFileUsage.VIEW_TOP,
    usage_1.ProjectFileUsage.VIEW_RIGHT,
    usage_1.ProjectFileUsage.VIEW_LEFT,
    usage_1.ProjectFileUsage.VIEW_FRONT,
    usage_1.ProjectFileUsage.VIEW_3D_INSERTION,
    usage_1.ProjectFileUsage.UNDEFINED,
];
/** Matrix imagery the office arranges (perspective renders, material boards). */
const MATRIX_IMAGERY = [
    usage_1.ProjectFileUsage.PERSPECTIVE,
    usage_1.ProjectFileUsage.CLOSE_PERSPECTIVE,
    usage_1.ProjectFileUsage.MATERIALS,
];
/** Town-planning material the office keeps to itself. */
const OFFICE_ONLY = [
    usage_1.ProjectFileUsage.CADASTRAL_MAP,
    usage_1.ProjectFileUsage.HISTORICAL_MONUMENT_MAP,
    usage_1.ProjectFileUsage.INTERNALS,
];
/** Town-planning analyses the client side may replace but does not browse. */
const CLIENT_WRITABLE_ANALYSES = [
    usage_1.ProjectFileUsage.AUTHORIZED_COLOR,
    usage_1.ProjectFileUsage.GEOGRAPHIC_RISK_REPORT,
    usage_1.ProjectFileUsage.PPRI,
];
/**
 * UA-derived pieces the commercial may consult only when the study was ordered
 * by the client side — a study the architect ordered is the office's own working
 * material. Replaces the hard-coded `SALESMAN_UA_REQUESTOR_USAGES` set that
 * lived in `useDocumentsView`.
 */
const UA_DERIVED_PIECES = [
    usage_1.ProjectFileUsage.AUTHORIZED_COLOR,
    usage_1.ProjectFileUsage.GEOGRAPHIC_RISK_REPORT,
    usage_1.ProjectFileUsage.URBAN_ANALYSIS,
    usage_1.ProjectFileUsage.CADASTRAL_MAP,
];
/** Every usage in the town-planning section, whatever the role. */
const TOWN_PLANNING_PIECES = [
    usage_1.ProjectFileUsage.CADASTRAL_MAP,
    usage_1.ProjectFileUsage.HISTORICAL_MONUMENT_MAP,
    usage_1.ProjectFileUsage.PLU,
    usage_1.ProjectFileUsage.PPRI,
    usage_1.ProjectFileUsage.AUTHORIZED_COLOR,
    usage_1.ProjectFileUsage.GEOGRAPHIC_RISK_REPORT,
    usage_1.ProjectFileUsage.URBAN_ANALYSIS,
];
const OFFICE_ROLES = [
    account_type_1.AccountType.ARCHITECT,
    account_type_1.AccountType.COLLABORATOR,
];
const CLIENT_SIDE_ROLES = [
    account_type_1.AccountType.CLIENT,
    account_type_1.AccountType.SALESMAN,
];
/** The roles that share the commercial hub surface. */
const HUB_ROLES = [
    account_type_1.AccountType.SALESMAN,
    account_type_1.AccountType.SOCIETY,
];
const grant = (usages, accountTypes, steps, facet) => ({
    usages,
    accountTypes,
    ...(steps === 'ALL' ? {} : { steps }),
    grant: facet,
});
const visibleAt = (usages, accountTypes, steps) => grant(usages, accountTypes, steps, { visible: true });
const editableAt = (usages, accountTypes, steps) => grant(usages, accountTypes, steps, { editable: true });
// ── Layer 1: the base role × step windows ───────────────────────────────────
/**
 * Reproduces the pre-existing access matrices exactly — the characterization
 * test asserts all 5,525 (usage × role × step) triples are unchanged. The rules
 * that alter behaviour are the later layers.
 *
 * A note on the windows below where `editable` opens at a step the usage is not
 * yet `visible` at (matrix imagery, implantation, the client-writable analyses):
 * that predates this module and is preserved verbatim. It is inert on the
 * documents page, which consults a row's CTA only after the row passed the
 * visibility filter. The characterization test pins the exact set so no NEW
 * instance can appear unnoticed.
 */
const BASE_ACCESS_RULES = [
    // ── the architect's dossier ──
    visibleAt(ARCHITECT_DOSSIER_PIECES, OFFICE_ROLES, DRAFTING_ONWARDS),
    visibleAt(ARCHITECT_DOSSIER_PIECES, [account_type_1.AccountType.CLIENT], DEPOSITED_ONWARDS),
    visibleAt(ARCHITECT_DOSSIER_PIECES, [account_type_1.AccountType.SALESMAN], DOSSIER_READY_ONWARDS),
    // Drafting is architect-only; the collaborator follows read-only.
    editableAt(ARCHITECT_DOSSIER_PIECES, [account_type_1.AccountType.ARCHITECT], [
        steps_1.ProjectStep.PROJECT_IN_PROGRESS,
    ]),
    // ── matrix imagery ──
    visibleAt(MATRIX_IMAGERY, OFFICE_ROLES, DRAFTING_ONWARDS),
    visibleAt(MATRIX_IMAGERY, [account_type_1.AccountType.CLIENT], DEPOSITED_ONWARDS),
    visibleAt(MATRIX_IMAGERY, [account_type_1.AccountType.SALESMAN], DOSSIER_READY_ONWARDS),
    editableAt(MATRIX_IMAGERY, OFFICE_ROLES, [
        steps_1.ProjectStep.PROJECT_INITIALIZED,
        steps_1.ProjectStep.PROJECT_IN_PROGRESS,
    ]),
    editableAt(MATRIX_IMAGERY, CLIENT_SIDE_ROLES, [
        steps_1.ProjectStep.PROJECT_INITIALIZED,
    ]),
    // ── implantation (a matrix piece the client side may supply at intake) ──
    visibleAt([usage_1.ProjectFileUsage.IMPLANTATION], OFFICE_ROLES, DRAFTING_ONWARDS),
    visibleAt([usage_1.ProjectFileUsage.IMPLANTATION], [account_type_1.AccountType.CLIENT], DEPOSITED_ONWARDS),
    visibleAt([usage_1.ProjectFileUsage.IMPLANTATION], [account_type_1.AccountType.SALESMAN], DOSSIER_READY_ONWARDS),
    editableAt([usage_1.ProjectFileUsage.IMPLANTATION], OFFICE_ROLES, 'ALL'),
    editableAt([usage_1.ProjectFileUsage.IMPLANTATION], CLIENT_SIDE_ROLES, INTAKE_STEPS),
    // ── the client-side intake set ──
    visibleAt(CLIENT_INTAKE_PIECES, OFFICE_ROLES, 'ALL'),
    visibleAt(CLIENT_INTAKE_PIECES, CLIENT_SIDE_ROLES, 'ALL'),
    editableAt(CLIENT_INTAKE_PIECES, OFFICE_ROLES, 'ALL'),
    editableAt(CLIENT_INTAKE_PIECES, CLIENT_SIDE_ROLES, INTAKE_STEPS),
    // ── the existing-state survey (the one piece a company also browses) ──
    visibleAt([usage_1.ProjectFileUsage.EXISTING_PLANS], OFFICE_ROLES, 'ALL'),
    visibleAt([usage_1.ProjectFileUsage.EXISTING_PLANS], CLIENT_SIDE_ROLES, 'ALL'),
    visibleAt([usage_1.ProjectFileUsage.EXISTING_PLANS], [account_type_1.AccountType.SOCIETY], 'ALL'),
    editableAt([usage_1.ProjectFileUsage.EXISTING_PLANS], OFFICE_ROLES, 'ALL'),
    editableAt([usage_1.ProjectFileUsage.EXISTING_PLANS], CLIENT_SIDE_ROLES, INTAKE_STEPS),
    // ── the project sheet (office-produced, everyone else reads it) ──
    visibleAt([usage_1.ProjectFileUsage.PROJECT_SHEET], OFFICE_ROLES, 'ALL'),
    visibleAt([usage_1.ProjectFileUsage.PROJECT_SHEET], CLIENT_SIDE_ROLES, 'ALL'),
    editableAt([usage_1.ProjectFileUsage.PROJECT_SHEET], OFFICE_ROLES, 'ALL'),
    // ── town planning ──
    visibleAt(TOWN_PLANNING_PIECES, OFFICE_ROLES, 'ALL'),
    editableAt(TOWN_PLANNING_PIECES, OFFICE_ROLES, 'ALL'),
    visibleAt([usage_1.ProjectFileUsage.PLU, usage_1.ProjectFileUsage.URBAN_ANALYSIS], [account_type_1.AccountType.CLIENT], 'ALL'),
    visibleAt([usage_1.ProjectFileUsage.URBAN_ANALYSIS], [account_type_1.AccountType.SOCIETY], 'ALL'),
    editableAt(CLIENT_WRITABLE_ANALYSES, [account_type_1.AccountType.CLIENT, account_type_1.AccountType.SOCIETY], 'ALL'),
    // ── office-only material ──
    visibleAt(OFFICE_ONLY, OFFICE_ROLES, 'ALL'),
    editableAt(OFFICE_ONLY, OFFICE_ROLES, 'ALL'),
    // ── the deposit receipt ──
    visibleAt([usage_1.ProjectFileUsage.DEPOSIT_RECEIPT], OFFICE_ROLES, POST_VALIDATION_STEPS),
    editableAt([usage_1.ProjectFileUsage.DEPOSIT_RECEIPT], OFFICE_ROLES, POST_VALIDATION_STEPS),
    visibleAt([usage_1.ProjectFileUsage.DEPOSIT_RECEIPT], [account_type_1.AccountType.SALESMAN], POST_VALIDATION_STEPS),
];
// ── Layer 2: the hub read-back rules ────────────────────────────────────────
/**
 * Outside the window where a hub role may actually upload, the documents page is
 * a read-back of what was provided: a slot appears only once it holds a
 * document. Inside their upload window every slot shows, empty included — there
 * it is the commercial's checklist of what the client still owes.
 *
 * Derived from layer 1 rather than restated, so the checklist can never drift
 * from what the role may actually upload, and so the read-back can only ever
 * NARROW layer 1 — a slot layer 1 hides never becomes visible here.
 *
 * ARCHITECT and COLLABORATOR are deliberately untouched: the "Manquants" filter
 * pill and the missing-document alerts exist precisely to surface empty required
 * slots. CLIENT keeps today's behaviour; adding it is a one-entry change.
 */
const hubReadBackRules = () => {
    const rules = [];
    for (const role of HUB_ROLES) {
        for (const step of ALL_STEPS) {
            const base = (usage) => resolveFrom(BASE_ACCESS_RULES, usage, { accountType: role, step });
            const checklist = [];
            const readBack = [];
            for (const usage of ALL_USAGES) {
                const resolved = base(usage);
                if (!resolved.visible)
                    continue;
                if (resolved.editable)
                    checklist.push(usage);
                else
                    readBack.push(usage);
            }
            if (readBack.length > 0) {
                rules.push({
                    usages: readBack,
                    accountTypes: [role],
                    steps: [step],
                    when: ['non-empty'],
                    grant: { visible: true },
                });
                rules.push({
                    usages: readBack,
                    accountTypes: [role],
                    steps: [step],
                    when: [{ not: 'non-empty' }],
                    grant: { visible: false },
                });
            }
            // `checklist` needs no rule: layer 1 already grants it unconditionally.
            void checklist;
        }
    }
    return rules;
};
// ── Layer 3: the context rules lifted out of useDocumentsView ───────────────
const CONTEXT_RULES = [
    // The town-planning section is only meaningful once a study was delivered.
    {
        usages: TOWN_PLANNING_PIECES,
        when: [{ not: 'urban-analysis-delivered' }],
        grant: { visible: false },
    },
    // The commercial may consult the UA-derived pieces only when the study came
    // from the client side, it has been delivered, and the slot holds something.
    // SALESMAN only — SOCIETY's window is unchanged by this rule.
    {
        usages: UA_DERIVED_PIECES,
        accountTypes: [account_type_1.AccountType.SALESMAN],
        when: [
            'urban-analysis-ordered-by-client-side',
            'urban-analysis-delivered',
            'non-empty',
        ],
        grant: { visible: true },
    },
];
// ── Layer 3a: the dossier's shared documents (#883) ────────────────────────
/**
 * The exchange space of a dossier: the office and the commercial side see it at
 * every step; the office adds at every step, the commercial side at every step
 * but an abandoned dossier. A client never sees it — Hasura and `canAccessFile`
 * hold that line too, this is only what the SPA renders.
 *
 * After the hub read-back layer on purpose: an empty space is still where a
 * document goes, so it is never narrowed to "only once it holds one". No
 * requirement, no archive facet: a shared document is never a dossier piece.
 */
const SHARED_DOCUMENT_RULES = [
    visibleAt([usage_1.ProjectFileUsage.PROJECT_SHARED_DOCUMENT], [...OFFICE_ROLES, ...HUB_ROLES, account_type_1.AccountType.CLIENT], 'ALL'),
    editableAt([usage_1.ProjectFileUsage.PROJECT_SHARED_DOCUMENT], OFFICE_ROLES, 'ALL'),
    editableAt([usage_1.ProjectFileUsage.PROJECT_SHARED_DOCUMENT], [...HUB_ROLES, account_type_1.AccountType.CLIENT], shared_documents_1.PROJECT_SHARED_DOCUMENTS_OPEN_STEPS),
    { usages: [usage_1.ProjectFileUsage.PROJECT_SHARED_DOCUMENT], grant: { uploadPages: ['documents'] } },
];
// ── Layer 3b: what the commercial side never reaches ────────────────────────
/**
 * Usages the commercial side must never see, whoever ordered the study (#654).
 *
 * The PLU extract is the office's own working material: it is what the study is
 * BUILT from, not what the study delivers, and handing it over invites a
 * commercial to read the zoning rules themselves instead of the verdict the
 * architect wrote. "Even if they ordered the urban analysis" is the whole point
 * — paying for the study buys the study, not its sources.
 *
 * Stated as a revoke rather than as an omission: today no rule grants PLU to a
 * hub role, so this changes nothing on its own. It is here so that a later rule
 * widening what the commercial may consult — the way `UA_DERIVED_PIECES` widened
 * it once already — cannot quietly take the PLU with it.
 *
 * Scope, so nobody reads more into it than it does: this governs what the SPA
 * RENDERS. Hasura still serves a PLU row to a SALESMAN or SOCIETY that asks for
 * it directly, and the file-download gate still answers on ownership alone — a
 * deliberate call on #654, which is about what the commercial is shown. The one
 * place it is enforced away from the browser is the study delivery
 * (`backend/src/features/urban-analysis/constants/delivery.ts`), because an
 * email is not a screen the frontend can filter.
 */
exports.HUB_FORBIDDEN_USAGES = [
    usage_1.ProjectFileUsage.PLU,
];
/** Applied last, so no earlier layer can grant what this one takes away. */
const HUB_REVOKED_RULES = [
    {
        usages: exports.HUB_FORBIDDEN_USAGES,
        accountTypes: HUB_ROLES,
        grant: { visible: false, editable: false },
    },
];
// ── Layer 4: expected file counts ───────────────────────────────────────────
/**
 * Per-usage expected file count — drives the ledger tally and badge. Each count
 * is a MINIMUM: a slot is complete once its non-rejected files reach it, and
 * surplus photos beyond it stay counted rather than being flagged (#583). The
 * street set asks for 3, the house and garden sets for 6.
 */
const REQUIRED_COUNT_RULES = [
    {
        usages: [usage_1.ProjectFileUsage.HOUSE_PICTURE, usage_1.ProjectFileUsage.GARDEN_PICTURE],
        grant: { required: 6 },
    },
    {
        usages: [usage_1.ProjectFileUsage.STREET_PICTURE],
        grant: { required: 3 },
    },
    {
        usages: [
            usage_1.ProjectFileUsage.CLIENT_PROXY,
            usage_1.ProjectFileUsage.SALES_FILE,
            usage_1.ProjectFileUsage.EXISTING_PLANS,
            usage_1.ProjectFileUsage.VIEW_TOP,
            usage_1.ProjectFileUsage.VIEW_RIGHT,
            usage_1.ProjectFileUsage.VIEW_LEFT,
            usage_1.ProjectFileUsage.VIEW_FRONT,
            usage_1.ProjectFileUsage.VIEW_3D_INSERTION,
            usage_1.ProjectFileUsage.URBAN_ANALYSIS,
            usage_1.ProjectFileUsage.CERFA_DP,
            usage_1.ProjectFileUsage.CERFA_PC,
            usage_1.ProjectFileUsage.CERFA_PCM,
            usage_1.ProjectFileUsage.OTHER_APPLICANTS_SHEET,
            usage_1.ProjectFileUsage.PC_MATRIX,
            usage_1.ProjectFileUsage.PCMI1,
            usage_1.ProjectFileUsage.PCMI2,
            usage_1.ProjectFileUsage.PCMI2_1,
            usage_1.ProjectFileUsage.PCMI3,
            usage_1.ProjectFileUsage.PCMI4,
            usage_1.ProjectFileUsage.PCMI5_1,
            usage_1.ProjectFileUsage.PCMI5_2,
            usage_1.ProjectFileUsage.PCMI5_3,
            usage_1.ProjectFileUsage.PCMI5_4,
            usage_1.ProjectFileUsage.PCMI6,
            usage_1.ProjectFileUsage.PCMI78,
            usage_1.ProjectFileUsage.DP_MATRIX,
            usage_1.ProjectFileUsage.DP1,
            usage_1.ProjectFileUsage.DP2,
            usage_1.ProjectFileUsage.DP2_1,
            usage_1.ProjectFileUsage.DP3,
            usage_1.ProjectFileUsage.DP4_1,
            usage_1.ProjectFileUsage.DP4_2,
            usage_1.ProjectFileUsage.DP4_3,
            usage_1.ProjectFileUsage.DP4_4,
            usage_1.ProjectFileUsage.DP6,
            usage_1.ProjectFileUsage.DP78,
            usage_1.ProjectFileUsage.DP11,
        ],
        grant: { required: 1 },
    },
];
/**
 * Per-project-type opt-outs, derived from FILE_REQUIREMENT_RULES so the ledger,
 * the commercial document slots and the backend send-gate agree on what a
 * project type sheds (a POOL drops the drawing views, the 3D insertion, the
 * existing survey and the commercial dossier).
 */
const projectTypeOptOutRules = () => {
    const projectTypes = Array.from(new Set(requirements_1.FILE_REQUIREMENT_RULES.flatMap((rule) => rule.projectType ? [rule.projectType] : [])));
    const base = (0, requirements_1.resolveRequiredFiles)(requirements_1.FILE_REQUIREMENT_RULES, {
        step: steps_1.ProjectStep.PROJECT_INITIALIZED,
    });
    const rules = [];
    for (const projectType of projectTypes) {
        const forType = (0, requirements_1.resolveRequiredFiles)(requirements_1.FILE_REQUIREMENT_RULES, {
            step: steps_1.ProjectStep.PROJECT_INITIALIZED,
            projectType,
        });
        const shed = Object.keys(base).filter((usage) => !(usage in forType));
        if (shed.length === 0)
            continue;
        rules.push({
            usages: shed,
            projectTypes: [projectType],
            grant: { required: 0 },
        });
    }
    return rules;
};
// ── Layer 5: upload surfaces ────────────────────────────────────────────────
/**
 * Plan and façade slots the matrix page owns: they are placed and scaled in the
 * matrix editor, never dropped onto the documents page.
 */
const MATRIX_PLAN_SLOTS = [
    usage_1.ProjectFileUsage.GROUND_PLAN,
    usage_1.ProjectFileUsage.CURRENT_GROUND_PLAN,
    usage_1.ProjectFileUsage.FUTUR_GROUND_PLAN,
    usage_1.ProjectFileUsage.FIELD_CUT,
    usage_1.ProjectFileUsage.CITY_LOCATION_PLAN,
    usage_1.ProjectFileUsage.DISTRICT_LOCATION_PLAN,
    usage_1.ProjectFileUsage.CURRENT_FACADE_1,
    usage_1.ProjectFileUsage.CURRENT_FACADE_2,
    usage_1.ProjectFileUsage.CURRENT_FACADE_3,
    usage_1.ProjectFileUsage.CURRENT_FACADE_4,
    usage_1.ProjectFileUsage.CURRENT_FACADE_5,
    usage_1.ProjectFileUsage.CURRENT_FACADE_6,
    usage_1.ProjectFileUsage.CURRENT_FACADE_7,
    usage_1.ProjectFileUsage.CURRENT_FACADE_8,
    usage_1.ProjectFileUsage.FUTUR_FACADE_1,
    usage_1.ProjectFileUsage.FUTUR_FACADE_2,
    usage_1.ProjectFileUsage.FUTUR_FACADE_3,
    usage_1.ProjectFileUsage.FUTUR_FACADE_4,
    usage_1.ProjectFileUsage.FUTUR_FACADE_5,
    usage_1.ProjectFileUsage.FUTUR_FACADE_6,
    usage_1.ProjectFileUsage.FUTUR_FACADE_7,
    usage_1.ProjectFileUsage.FUTUR_FACADE_8,
];
/**
 * Slots the application fills itself — the assembled CERFA annexes and the
 * urban study. Nobody uploads to them, which an empty page list states exactly:
 * they contribute no requirement to a section's tally and offer no upload CTA.
 */
const SYSTEM_GENERATED = [
    usage_1.ProjectFileUsage.URBAN_ANALYSIS,
    usage_1.ProjectFileUsage.PCMI1,
    usage_1.ProjectFileUsage.PCMI2,
    usage_1.ProjectFileUsage.PCMI2_1,
    usage_1.ProjectFileUsage.PCMI3,
    usage_1.ProjectFileUsage.PCMI4,
    usage_1.ProjectFileUsage.PCMI5_1,
    usage_1.ProjectFileUsage.PCMI5_2,
    usage_1.ProjectFileUsage.PCMI5_3,
    usage_1.ProjectFileUsage.PCMI5_4,
    usage_1.ProjectFileUsage.PCMI6,
    usage_1.ProjectFileUsage.PCMI78,
    usage_1.ProjectFileUsage.DP1,
    usage_1.ProjectFileUsage.DP2,
    usage_1.ProjectFileUsage.DP2_1,
    usage_1.ProjectFileUsage.DP3,
    usage_1.ProjectFileUsage.DP4_1,
    usage_1.ProjectFileUsage.DP4_2,
    usage_1.ProjectFileUsage.DP4_3,
    usage_1.ProjectFileUsage.DP4_4,
    usage_1.ProjectFileUsage.DP6,
    usage_1.ProjectFileUsage.DP78,
    usage_1.ProjectFileUsage.DP11,
];
/** Where each usage may be uploaded from. */
const UPLOAD_PAGE_RULES = [
    {
        usages: [
            ...MATRIX_IMAGERY,
            ...MATRIX_PLAN_SLOTS,
            usage_1.ProjectFileUsage.IMPLANTATION,
            usage_1.ProjectFileUsage.PICTURE_1,
            usage_1.ProjectFileUsage.PICTURE_2,
            usage_1.ProjectFileUsage.PICTURE_3,
            usage_1.ProjectFileUsage.PICTURE_4,
            usage_1.ProjectFileUsage.PICTURE_5,
            usage_1.ProjectFileUsage.PICTURE_6,
        ],
        grant: { uploadPages: ['matrix'] },
    },
    {
        usages: [usage_1.ProjectFileUsage.EXISTING_PLANS],
        grant: { uploadPages: ['documents', 'matrix'] },
    },
    // Listed last so it wins over any page grant above.
    { usages: SYSTEM_GENERATED, grant: { uploadPages: [] } },
];
/** Per-usage MIME allowlists, adapted from the existing single source. */
const mimeTypeRules = () => Object.keys(mime_1.USAGE_ALLOWED_MIME_TYPES).flatMap((usage) => {
    const mimeTypes = mime_1.USAGE_ALLOWED_MIME_TYPES[usage];
    return mimeTypes ? [{ usages: [usage], grant: { mimeTypes } }] : [];
});
// ── Layer 5b: the sent-dossier archive ──────────────────────────────────────
/**
 * What the mairie receives, and so what « Tout télécharger (.zip) » packs
 * (#799): the CERFA, its co-applicant annexe and the matrice. The matrice
 * already IS the plan set, so the individual sheets stay out; the récépissé is
 * what the mairie hands BACK, so it stays out too.
 */
exports.SENT_DOSSIER_USAGES = [
    usage_1.ProjectFileUsage.CERFA_DP,
    usage_1.ProjectFileUsage.CERFA_PC,
    usage_1.ProjectFileUsage.CERFA_PCM,
    usage_1.ProjectFileUsage.CERFA_13703_11,
    usage_1.ProjectFileUsage.CERFA_13411_11,
    usage_1.ProjectFileUsage.CERFA_13406_12,
    usage_1.ProjectFileUsage.OTHER_APPLICANTS_SHEET,
    usage_1.ProjectFileUsage.PC_MATRIX,
    usage_1.ProjectFileUsage.DP_MATRIX,
];
/**
 * From the hand-over on. Before PROJECT_FILE_SENT there is no sent dossier to
 * archive — only a draft. Visibility still decides who gets it (see
 * `ResolvedDocumentAccess.archived`).
 */
const ARCHIVE_RULES = [
    {
        usages: exports.SENT_DOSSIER_USAGES,
        steps: POST_VALIDATION_STEPS,
        grant: { archived: true },
    },
];
// ── The rule list ───────────────────────────────────────────────────────────
/**
 * In precedence order — later rules override earlier ones, per facet:
 *   1. base role × step access
 *   2. the hub read-back narrowing
 *   3. the context rules (urban analysis), then the shared documents (#883)
 *   4. expected counts, then the per-project-type opt-outs
 *   5. upload pages and MIME allowlists, then the sent-dossier archive
 *   6. the commercial-side revoke (#654), last so nothing can undo it
 */
/**
 * A company is treated exactly as the salesmen it owns (#379).
 *
 * Every rule naming SALESMAN also names SOCIETY. Applied here rather than at
 * each `accountTypes` literal so the product rule lives in one place, a new
 * rule inherits it, and — because it maps the ASSEMBLED list — a later rule
 * that narrows or revokes something for a salesman narrows it for the company
 * too. Rules naming SOCIETY on its own (documents its salesmen never see) are
 * untouched.
 *
 * Without it the company hub is subtly inert: the intake pieces are granted to
 * `CLIENT_SIDE_ROLES`, so a company saw no upload control on the documents page
 * and could never supply a required file, while the salesman who owns the very
 * same dossier could.
 */
const alsoGrantedToCompany = (rules) => rules.map((rule) => rule.accountTypes?.includes(account_type_1.AccountType.SALESMAN) &&
    !rule.accountTypes.includes(account_type_1.AccountType.SOCIETY)
    ? { ...rule, accountTypes: [...rule.accountTypes, account_type_1.AccountType.SOCIETY] }
    : rule);
exports.DOCUMENT_ACCESS_RULES = alsoGrantedToCompany([
    ...BASE_ACCESS_RULES,
    ...hubReadBackRules(),
    ...CONTEXT_RULES,
    ...SHARED_DOCUMENT_RULES,
    ...REQUIRED_COUNT_RULES,
    ...projectTypeOptOutRules(),
    ...UPLOAD_PAGE_RULES,
    ...mimeTypeRules(),
    ...ARCHIVE_RULES,
    ...HUB_REVOKED_RULES,
]);
/** The base layer alone — used by the characterization test. */
exports.BASE_DOCUMENT_ACCESS_RULES = BASE_ACCESS_RULES;
/** Resolve every facet for one usage in one context. */
const resolveDocumentAccess = (usage, ctx = {}) => resolveFrom(exports.DOCUMENT_ACCESS_RULES, usage, ctx);
exports.resolveDocumentAccess = resolveDocumentAccess;
/** Resolve against an explicit rule list — for tests and layered checks. */
exports.resolveDocumentAccessFrom = resolveFrom;
/**
 * True when the project type sheds this usage entirely (a POOL and the drawing
 * views). Such a slot carries no requirement, so an empty one is not "missing"
 * and the ledger drops the row rather than asking for it.
 */
const isUsageOptedOutForProjectType = (usage, projectType) => {
    if (projectType == null)
        return false;
    const base = (0, exports.resolveDocumentAccess)(usage).required;
    const forType = (0, exports.resolveDocumentAccess)(usage, { projectType }).required;
    return base > 0 && forType === 0;
};
exports.isUsageOptedOutForProjectType = isUsageOptedOutForProjectType;
//# sourceMappingURL=access.js.map